import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Phone,
  AlertTriangle,
  CheckCircle,
  Heart,
  ArrowLeft,
  Shield,
  EyeOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickHelp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    description: "",
    zip_code: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [helpId, setHelpId] = useState(null);
  const [respondersNotified, setRespondersNotified] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Basic phone validation
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/quick-help`, formData);
      setHelpId(response.data.quick_help_id);
      setRespondersNotified(response.data.responders_notified);
      setSubmitted(true);
      toast.success("Help request sent! A responder will call you shortly.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request. Please try again or call 988.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#112e51] to-[#1a4480]">
        <div className="container mx-auto px-4 py-8 max-w-lg">
          {/* Success Card */}
          <Card className="bg-white shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                Help is On The Way!
              </h2>
              <p className="text-gray-600 mb-6">
                Your request has been received. {respondersNotified > 0 ? (
                  <span className="font-semibold text-green-600">{respondersNotified} responder{respondersNotified > 1 ? 's have' : ' has'} been notified</span>
                ) : (
                  <span>Our team has been notified</span>
                )} and will call you at <span className="font-semibold">{formData.phone}</span> shortly.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-amber-800 mb-2">
                  <Phone className="w-5 h-5" />
                  <span className="font-semibold">Keep your phone nearby</span>
                </div>
                <p className="text-sm text-amber-700">
                  A VALORLink responder will call you from a local number
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-700 font-medium">
                  If you're in immediate danger, call <a href="tel:911" className="underline font-bold">911</a> or <a href="tel:988" className="underline font-bold">988</a> (Veterans Press 1)
                </p>
              </div>

              <div className="space-y-3">
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Return to Home
                  </Button>
                </Link>
              </div>

              <p className="text-xs text-gray-500 mt-6">
                Reference ID: {helpId}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#112e51] to-[#1a4480]">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/20">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-lg">
        {/* Emergency Banner */}
        <div className="bg-red-600 text-white rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-semibold">If you're in immediate danger</p>
            <p className="text-sm">Call <a href="tel:911" className="underline font-bold">911</a> or <a href="tel:988" className="underline font-bold">988</a> (Press 1 for Veterans)</p>
          </div>
        </div>

        {/* Main Form Card */}
        <Card className="bg-white shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
              Get Help Now
            </CardTitle>
            <CardDescription>
              No account needed. A trained peer responder will call you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Your Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="h-12"
                  data-testid="quick-help-name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="h-12"
                  data-testid="quick-help-phone"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  A responder will call you at this number
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">
                  What's going on? *
                </Label>
                <Textarea
                  id="description"
                  placeholder="Briefly describe what you're experiencing and how we can help..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="min-h-[100px]"
                  data-testid="quick-help-description"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="zip_code" className="text-sm font-medium">
                  Zip Code <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="zip_code"
                  type="text"
                  placeholder="12345"
                  maxLength={10}
                  value={formData.zip_code}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  className="h-12"
                  data-testid="quick-help-zip"
                />
                <p className="text-xs text-muted-foreground">
                  Helps us connect you with nearby responders
                </p>
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full h-14 text-lg bg-primary hover:bg-primary/90"
                data-testid="quick-help-submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5 mr-2" />
                    Request a Call
                  </>
                )}
              </Button>
            </form>

            {/* Trust indicators */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-green-600" />
                <span>All responders are trained & verified</span>
              </div>
            </div>

            {/* Anonymous Option */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/anonymous-help")}
                data-testid="anonymous-help-btn"
              >
                <EyeOff className="w-4 h-4 mr-2" />
                Prefer to stay anonymous?
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Get help without sharing your identity
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <p className="text-center text-white/70 text-xs mt-6 px-4">
          VALORLink provides peer support from trained veteran volunteers. 
          This is not a clinical, medical, or emergency service.
        </p>
      </main>
    </div>
  );
};

export default QuickHelp;
